/**
 * @module app.shared
 */

import { autoinject, bindable } from 'aurelia-framework';
import { focusFirst, focusLast } from 'app/shared/lib/focus';
import { CustomAttributeBase } from 'app/shared/attributes/abstracts/custom-attribute-base';

const focusHelperClass = 'ess-focus-helper';
const focusHelperHTML = `<div class="${focusHelperClass}" tabindex="0"></div>`;

/**
 * @class
 * Captures focus within an element.
 */
@autoinject()
export class EssCaptureFocusCustomAttribute extends CustomAttributeBase {

    protected mutationObserver: MutationObserver = new MutationObserver(this.handleMutation.bind(this));

    protected handleFocus = ({ target, relatedTarget }: FocusEvent) => {
        if(target instanceof Node && relatedTarget instanceof Node) {
            let focus: Function | any;
            // on start focus, if related target is after start, focus last,
            if(target === this.element.firstChild) {
                focus = target.compareDocumentPosition(relatedTarget) & 4 ? focusLast : focusFirst;
            }
            // on end focus, if related target is before end, focus first
            if(target === this.element.lastChild) {
                focus = target.compareDocumentPosition(relatedTarget) & 2 ? focusFirst : focusLast;
            }
            if(focus !== undefined) {
                Promise.resolve().then(() => focus(this.element, `:not(.${focusHelperClass})`));
            }
        }
    };

    protected attached() {
        if(!this.hasFocusHelpers(this.element)) {
            this.addFocusHelpers(this.element);
        }
        this.element.addEventListener('focus', this.handleFocus as EventListener, { capture: true });
        this.mutationObserver.observe(this.element, { childList: true });
    }

    public detached() {
        this.mutationObserver.disconnect();
        this.removeFocusHelpers(this.element);
        this.element.removeEventListener('focus', this.handleFocus as EventListener, { capture: true });
    }

    protected hasFocusHelpers(element: Element): boolean {
        return Array.from(element.children)
            .some((child) => this.isFocusHelper(child));
    }

    protected isFocusHelper(element: Element): boolean {
        return element.classList.contains(focusHelperClass);
    }

    protected addFocusHelpers(element: Element) {
        element.insertAdjacentHTML('afterbegin', focusHelperHTML);
        element.insertAdjacentHTML('beforeend', focusHelperHTML);
    }

    protected removeFocusHelpers(element: Element) {
        Array.from(element.children)
            .filter((child) => child.classList.contains(focusHelperClass))
            .forEach((child) => child.remove());
    }

    protected handleMutation(mutations: MutationRecord[], observer: any) {
        // When a new child element is added, ensure the last focus helper remains the last child element
        if(mutations.some(mutation => mutation.addedNodes.length > 0) && this.hasFocusHelpers(this.element)) {
            const lastFocusHelper = Array.from(this.element.children).filter((child) => this.isFocusHelper(child)).pop()!;
            if(this.element.lastElementChild !== lastFocusHelper) {
                // remove and re-add the last focus helper
                lastFocusHelper.remove();
                this.element.insertAdjacentHTML('beforeend', focusHelperHTML);
            }
        }
    }
}
